import {
  INVOICE_DATA_MODE_PARAM,
  INVOICE_DATA_UUID_PARAM,
  INVOICE_ERROR_MAX_LENGTH,
  INVOICE_ERROR_PARAM,
  InvoiceDataDeepLinkModes,
  ONLY_FAILED_PARAM,
  PAYMENT_UUID_PARAM,
} from '@subscriptions/presentation/constants';

const BLOCK_INVOICING_TAB = 'invoicing';

/**
 * Builds the deep link that opens a block's fiscal data sheet in retry mode.
 *
 * Produces `/subscriptions/blocks/<blockUuid>?tab=invoicing&invoiceDataUuid=<uuid>&invoiceDataMode=retry`,
 * which BlockDetailTabs resolves to the invoicing tab and useInvoiceDataDeepLink resolves to the
 * retry sheet. Param names come from the shared constants so the link and its reader cannot drift.
 *
 * A `paymentUuid` is appended as `paymentUuid` when present — it identifies the failed payment
 * the retry sheet re-runs, which the fiscal record uuid alone cannot pin down when a block has
 * several failed charges against the same fiscal data.
 *
 * An `errorMessage` is appended as `invoiceError` when present, so the retry sheet can show
 * why the payment failed. It is trimmed and truncated to INVOICE_ERROR_MAX_LENGTH — the
 * value is backend free text and ends up in a URL, so its length cannot be left unbounded.
 * Encoding is handled by URLSearchParams.
 *
 * @param {Object} params - Path parameters
 * @param {string} params.blockUuid - Block whose invoicing tab should open
 * @param {string} params.invoiceDataUuid - Fiscal record to preload in the sheet
 * @param {string} [params.paymentUuid] - Failed payment the retry action should re-run
 * @param {string} [params.errorMessage] - Payment failure reason to surface in the sheet
 * @param {boolean} [params.onlyFailed] - Narrows the retry to the CFDIs that failed
 * @returns {string} Absolute in-app path
 *
 * @example
 * buildInvoiceDataRetryPath({ blockUuid: 'blk-1', invoiceDataUuid: 'inv-1', paymentUuid: 'pay-1' });
 * // '/subscriptions/blocks/blk-1?tab=invoicing&invoiceDataUuid=inv-1&invoiceDataMode=retry&paymentUuid=pay-1'
 */
export const buildInvoiceDataRetryPath = ({
  blockUuid,
  errorMessage,
  invoiceDataUuid,
  onlyFailed,
  paymentUuid,
}: {
  blockUuid: string;
  errorMessage?: string | null;
  invoiceDataUuid: string;
  onlyFailed?: boolean;
  paymentUuid?: string | null;
}): string => {
  const params = new URLSearchParams({
    tab: BLOCK_INVOICING_TAB,
    [INVOICE_DATA_UUID_PARAM]: invoiceDataUuid,
    [INVOICE_DATA_MODE_PARAM]: InvoiceDataDeepLinkModes.RETRY,
  });

  if (paymentUuid) {
    params.set(PAYMENT_UUID_PARAM, paymentUuid);
  }

  // Only written when narrowing: its absence already means "re-issue the whole payment", so
  // spelling out the default would just make every link longer.
  if (onlyFailed) {
    params.set(ONLY_FAILED_PARAM, 'true');
  }

  // El mensaje llega redactado desde `PaymentHistoryApiMapper`, que es por donde entra a la app.
  const trimmedError = errorMessage?.trim();
  if (trimmedError) {
    params.set(INVOICE_ERROR_PARAM, trimmedError.slice(0, INVOICE_ERROR_MAX_LENGTH));
  }

  return `/subscriptions/blocks/${blockUuid}?${params.toString()}`;
};
